import { api } from './apiClient.js'
import { ApiError } from './errors.js'
import {
  db,
  getMeta,
  META_KEYS,
  type OutboxEntry,
  type OutboxStatus,
  type ActionClass
} from './localDb.js'
import { uuidv7 } from './ids.js'

/**
 * Ausgangskorb.
 *
 * Schreibende Aktionen landen zuerst hier und werden danach der Reihe nach
 * übertragen. Damit funktioniert jede Aktion der Klasse A auch offline, und
 * ein abgestürzter Browser verliert nichts.
 *
 * Drei Festlegungen, die zusammengehören:
 *
 *  1. Streng der Reihe nach, genau eine Aktion gleichzeitig unterwegs.
 *     Langsamer als parallel, aber die Reihenfolge stimmt immer und ein
 *     Fehler ist nachvollziehbar. Für ein Einzelentwicklerprodukt ist diese
 *     Abwägung eindeutig.
 *  2. Neue Datensätze bekommen ihre Kennung vom Client, damit eine
 *     Folgeaktion sie referenzieren kann, ohne auf den Server zu warten.
 *  3. Ein dauerhaft fehlgeschlagener Eintrag blockiert die Warteschlange
 *     nicht – er wandert in eine Fehlerliste und der Rest läuft weiter.
 */

/** 2 s, 5 s, 15 s, 60 s, 5 min, danach alle 15 min. */
export const BACKOFF_MS = [2_000, 5_000, 15_000, 60_000, 300_000, 900_000]
/** Ab hier bekommt der Benutzer einen deutlichen Hinweis statt stiller Wiederholung. */
export const ATTEMPTS_BEFORE_NOTICE = 5
export const MAX_ATTEMPTS = 20

export function backoffFor(attempts: number): number {
  return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)]
}

export interface EnqueueInput {
  type: string
  actionClass: ActionClass
  method: 'POST' | 'PATCH' | 'PUT'
  path: string
  payload: unknown
  tenantId: string
  userId: string
  dependsOn?: string | null
  attachments?: string[]
  /** Nur setzen, wenn der Aufrufer die Kennung schon braucht (z.B. für Verweise). */
  id?: string
}

export async function enqueue(input: EnqueueInput): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: input.id ?? uuidv7(),
    createdAt: Date.now(),
    tenantId: input.tenantId,
    userId: input.userId,
    type: input.type,
    actionClass: input.actionClass,
    method: input.method,
    path: input.path,
    payload: input.payload,
    status: 'local',
    attempts: 0,
    nextAttemptAt: 0,
    lastError: null,
    lastErrorKind: null,
    serverResult: null,
    dependsOn: input.dependsOn ?? null,
    attachments: input.attachments ?? []
  }
  await db.outbox.add(entry)
  return entry
}

export interface OutboxCounts {
  pending: number
  failed: number
  conflict: number
  unclear: number
  sending: number
}

export async function counts(): Promise<OutboxCounts> {
  const all = await db.outbox.toArray()
  const by = (s: OutboxStatus): number => all.filter((e) => e.status === s).length
  return {
    pending: by('local') + by('waiting'),
    failed: by('failed'),
    conflict: by('conflict'),
    unclear: by('unclear'),
    sending: by('sending')
  }
}

/**
 * Beim Start: Einträge aufräumen, die mitten im Senden unterbrochen wurden.
 *
 * Wird die App geschlossen oder neu geladen, während eine Aktion unterwegs
 * ist, bleibt sie sonst für immer auf "sending" stehen und wird nie wieder
 * angefasst – der Rapport liegt still im Gerät und niemand merkt es.
 *
 * Erneut zu senden ist unbedenklich: die Aktionskennung ist dieselbe, und der
 * Server führt sie deshalb kein zweites Mal aus. Kam die Aktion beim ersten
 * Mal durch, liefert er einfach die gespeicherte Antwort zurück.
 */
export async function recoverInterrupted(): Promise<number> {
  const stuck = await db.outbox.where('status').equals('sending').toArray()
  for (const entry of stuck) {
    await db.outbox.update(entry.id, {
      status: 'waiting' as OutboxStatus,
      nextAttemptAt: 0,
      lastError: null
    })
  }
  return stuck.length
}

/** Einträge, die den Benutzer etwas angehen. */
export async function needsAttention(): Promise<OutboxEntry[]> {
  return db.outbox.where('status').anyOf('failed', 'conflict', 'unclear').toArray()
}

/** Fehlgeschlagenen Eintrag von Hand erneut versuchen. */
export async function retry(id: string): Promise<void> {
  await db.outbox.update(id, {
    status: 'waiting' as OutboxStatus,
    nextAttemptAt: 0,
    lastError: null
  })
}

/** Endgültig aufgeben – nur auf ausdrückliche Entscheidung des Benutzers. */
export async function discard(id: string): Promise<void> {
  await db.outbox.delete(id)
}

/**
 * Nächsten sendebereiten Eintrag holen, in Erfassungsreihenfolge.
 *
 * Und nur aus dem Betrieb, an den dieses Gerät gerade gebunden ist.
 *
 * Der Zusatz ist der zweite Riegel gegen denselben Fehler: Wer im selben
 * Browser den Betrieb wechselt, hat womöglich noch Einträge des alten im
 * Korb. Würden sie mit der neuen Sitzung losgeschickt, entstünde der Kunde
 * des einen Betriebs im Datenbestand eines anderen – und zwar mit einem
 * gültigen Berechtigungsnachweis, also ohne jede Fehlermeldung.
 *
 * Verworfen werden sie nicht. Dort liegt Arbeit, die es sonst nirgends gibt;
 * sie geht wieder los, sobald sich der zugehörige Betrieb anmeldet.
 */
async function nextDue(now: number): Promise<OutboxEntry | undefined> {
  const mandant = await getMeta<string | null>(META_KEYS.currentTenant, null)
  const candidates = await db.outbox.where('status').anyOf('local', 'waiting').sortBy('id') // UUID v7 sortiert zeitlich
  for (const entry of candidates) {
    if (mandant !== null && entry.tenantId !== mandant) continue
    if (entry.nextAttemptAt > now) continue
    if (entry.dependsOn) {
      const parent = await db.outbox.get(entry.dependsOn)
      // Vorgänger noch nicht durch: diesen Eintrag überspringen, nicht scheitern.
      if (parent && parent.status !== 'success') continue
    }
    return entry
  }
  return undefined
}

export interface DrainResult {
  sent: number
  failed: number
  stillPending: number
  /** Serverantworten, die der Abgleichmotor in den Cache schreibt. */
  results: Array<{ entry: OutboxEntry; result: unknown }>
}

let draining = false

/**
 * Ausgangskorb leeren. Genau eine Aktion gleichzeitig, und nur ein Durchlauf
 * gleichzeitig – ein zweiter Aufruf während des Leerens kehrt sofort zurück.
 */
export async function drain(options: { online?: boolean } = {}): Promise<DrainResult> {
  const result: DrainResult = { sent: 0, failed: 0, stillPending: 0, results: [] }
  if (draining) return result
  if (options.online === false) {
    result.stillPending = (await counts()).pending
    return result
  }
  draining = true

  try {
    for (;;) {
      const now = Date.now()
      const entry = await nextDue(now)
      if (!entry) break

      await db.outbox.update(entry.id, { status: 'sending' as OutboxStatus })

      try {
        const payload = await resolvePayload(entry)
        const answer = await api.send<unknown>(
          entry.path,
          entry.method,
          payload,
          entry.id,
          entry.actionClass === 'B' ? 60_000 : undefined
        )
        await db.outbox.update(entry.id, {
          status: 'success' as OutboxStatus,
          serverResult: answer,
          lastError: null,
          lastErrorKind: null
        })
        result.sent++
        result.results.push({ entry, result: answer })
      } catch (err) {
        const handled = await handleFailure(entry, err)
        if (handled === 'retry-later') {
          // Der Rest der Warteschlange wartet mit – die Reihenfolge zählt.
          break
        }
        if (handled === 'succeeded') {
          result.sent++
          const refreshed = await db.outbox.get(entry.id)
          if (refreshed) result.results.push({ entry: refreshed, result: refreshed.serverResult })
        } else {
          result.failed++
        }
      }
    }

    result.stillPending = (await counts()).pending
    return result
  } finally {
    draining = false
  }
}

/**
 * Verweise auf noch nicht übertragene Datensätze auflösen.
 * Da neue Kennungen vom Client kommen, ist das meistens nichts zu tun –
 * die Stelle existiert für Fälle, in denen der Server eine eigene Kennung
 * vergeben hat.
 */
async function resolvePayload(entry: OutboxEntry): Promise<unknown> {
  return entry.payload
}

type FailureOutcome = 'failed' | 'retry-later' | 'succeeded'

async function handleFailure(entry: OutboxEntry, err: unknown): Promise<FailureOutcome> {
  const attempts = entry.attempts + 1

  if (!(err instanceof ApiError)) {
    await db.outbox.update(entry.id, {
      status: 'failed' as OutboxStatus,
      attempts,
      lastError: 'Unerwarteter Fehler.',
      lastErrorKind: 'unknown'
    })
    return 'failed'
  }

  // Zustand nicht feststellbar: niemals blind wiederholen. Erst nachfragen.
  if (err.unclear) {
    const resolved = await probeStatus(entry, attempts)
    return resolved
  }

  if (err.kind === 'conflict') {
    await db.outbox.update(entry.id, {
      status: 'conflict' as OutboxStatus,
      attempts,
      lastError: err.message,
      lastErrorKind: err.kind
    })
    return 'failed'
  }

  if (!err.retryable || attempts >= MAX_ATTEMPTS) {
    await db.outbox.update(entry.id, {
      status: 'failed' as OutboxStatus,
      attempts,
      lastError: err.message,
      lastErrorKind: err.kind
    })
    return 'failed'
  }

  await db.outbox.update(entry.id, {
    status: 'waiting' as OutboxStatus,
    attempts,
    nextAttemptAt: Date.now() + backoffFor(entry.attempts),
    lastError: err.message,
    lastErrorKind: err.kind
  })
  return 'retry-later'
}

/**
 * Statusabfrage nach einer Zeitüberschreitung.
 *
 * Der Endpunkt /actions/:clientActionId sagt, ob die Aktion ausgeführt wurde.
 * Erst danach wird entschieden. Ohne diesen Schritt entstehen doppelte
 * Rechnungen und doppelte E-Mails.
 */
async function probeStatus(entry: OutboxEntry, attempts: number): Promise<FailureOutcome> {
  try {
    const status = await api.actionStatus(entry.id)
    if (status.state === 'ausgefuehrt') {
      await db.outbox.update(entry.id, {
        status: 'success' as OutboxStatus,
        attempts,
        serverResult: status.response,
        lastError: null,
        lastErrorKind: null
      })
      return 'succeeded'
    }
    if (status.state === 'laeuft') {
      await db.outbox.update(entry.id, {
        status: 'waiting' as OutboxStatus,
        attempts,
        nextAttemptAt: Date.now() + backoffFor(entry.attempts),
        lastError: 'Die Aktion läuft noch auf dem Server.',
        lastErrorKind: 'timeout'
      })
      return 'retry-later'
    }
    // Unbekannt: die Aktion kam nie an, also darf sie erneut gesendet werden.
    await db.outbox.update(entry.id, {
      status: 'waiting' as OutboxStatus,
      attempts,
      nextAttemptAt: Date.now() + backoffFor(entry.attempts),
      lastError: null,
      lastErrorKind: null
    })
    return 'retry-later'
  } catch {
    // Auch die Nachfrage scheitert – Zustand bleibt unklar und sichtbar.
    await db.outbox.update(entry.id, {
      status: 'unclear' as OutboxStatus,
      attempts,
      nextAttemptAt: Date.now() + backoffFor(entry.attempts),
      lastError: 'Ob das geklappt hat, konnte ich nicht feststellen. Ich frage beim Server nach.',
      lastErrorKind: 'timeout'
    })
    return 'failed'
  }
}

/**
 * Wann ist der nächste wartende Eintrag wieder dran?
 *
 * Ohne diese Angabe passiert nach einem fehlgeschlagenen Versuch bis zum
 * nächsten Takt gar nichts: der Monteur hat wieder Empfang, der erste Versuch
 * scheitert an einer Kleinigkeit, und der Rapport liegt weitere drei Minuten
 * herum, obwohl das Netz längst da ist.
 */
export async function nextWakeAt(): Promise<number | null> {
  const waiting = await db.outbox.where('status').anyOf('local', 'waiting').toArray()
  const times = waiting.map((e) => e.nextAttemptAt).filter((t) => t > 0)
  if (waiting.length === 0) return null
  if (times.length < waiting.length) return Date.now() // etwas ist sofort fällig
  return Math.min(...times)
}

/** Erfolgreiche Einträge nach einer Schonfrist entfernen. */
export async function prune(olderThanMs = 10 * 60_000): Promise<number> {
  const cutoff = Date.now() - olderThanMs
  const done = await db.outbox.where('status').equals('success').toArray()
  const stale = done.filter((e) => e.createdAt < cutoff).map((e) => e.id)
  await db.outbox.bulkDelete(stale)
  return stale.length
}
